import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Button from '../components/ui/Button';

const { FiSend, FiSmile, FiFrown, FiTrendingUp } = FiIcons;

const HowItWorks = () => {
  const steps = [
    {
      icon: FiSend,
      title: "Step 1 – Send Review Requests",
      description: "Automatically send SMS or email review requests after a purchase. Integrate seamlessly with your existing CRM or POS system to trigger requests without lifting a finger.",
      color: "bg-blue-100 text-blue-600"
    },
    {
      icon: FiSmile,
      title: "Step 2 – Capture Happy Customers",
      description: "Satisfied customers are directed to leave a Google review. Our smart routing system identifies happy customers and makes it one-click easy for them to post their 5-star rating publicly.",
      color: "bg-green-100 text-green-600"
    },
    {
      icon: FiFrown,
      title: "Step 3 – Manage Feedback",
      description: "Unhappy customers are redirected to private feedback. Instead of a public negative review, you receive direct, actionable feedback to resolve the issue privately and save the customer relationship.",
      color: "bg-orange-100 text-orange-600"
    },
    {
      icon: FiTrendingUp,
      title: "Step 4 – Grow Your Reputation",
      description: "Businesses get more positive reviews and higher rankings. Watch your local SEO soar as a steady stream of fresh, authentic Google reviews signals trust to search engines and new customers.",
      color: "bg-purple-100 text-purple-600"
    }
  ];

  return (
    <div className="py-20 bg-slate-50">
      <div className="container px-4 mx-auto max-w-5xl">
        <div className="max-w-3xl mx-auto mb-16 text-center">
          <h1 className="mb-6 text-4xl font-bold md:text-5xl text-primary">How It Works</h1>
          <p className="text-xl text-slate-600">A simple, four-step process to automate your reputation management and dominate local search.</p>
        </div>

        <div className="relative">
          {/* Connecting line for desktop */}
          <div className="absolute hidden w-1 h-full transform -translate-x-1/2 bg-slate-200 left-1/2 md:block md:top-10 md:bottom-10"></div>

          <div className="space-y-16">
            {steps.map((step, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                className={`flex flex-col md:flex-row items-center gap-8 md:gap-16 ${index % 2 !== 0 ? 'md:flex-row-reverse' : ''}`}
              >
                <div className="flex-1 w-full">
                  <div className="p-8 bg-white shadow-soft rounded-2xl">
                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 ${step.color}`}>
                      <SafeIcon icon={step.icon} className="w-8 h-8" />
                    </div>
                    <h3 className="mb-4 text-2xl font-bold text-primary">{step.title}</h3>
                    <p className="leading-relaxed text-slate-600">{step.description}</p>
                  </div>
                </div>
                
                {/* Center marker */}
                <div className="hidden md:flex flex-col items-center justify-center z-10 w-12">
                  <div className="w-8 h-8 border-4 border-white rounded-full bg-secondary shadow-md"></div>
                </div>

                <div className="flex-1 w-full">
                  {/* Visual diagram placeholder */}
                  <div className="flex items-center justify-center h-64 border-2 border-dashed rounded-2xl border-slate-300 bg-slate-100">
                    <span className="text-sm font-medium text-slate-400">Workflow Diagram {index + 1}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="mt-24 text-center">
          <h2 className="mb-8 text-3xl font-bold text-primary">Ready to automate your reviews?</h2>
          <Button to="/free-trial" variant="primary" size="lg">Start Your Free Trial</Button>
        </div>
      </div>
    </div>
  );
};

export default HowItWorks;